import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

/* --------------------------------- TAGS --------------------------------- */

export const listForumTags = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const [{ data: tags }, { data: links }] = await Promise.all([
    supabase.from("forum_tags").select("id, slug, name, kind, sort_order").order("sort_order"),
    supabase.from("forum_post_tags").select("tag_id"),
  ]);
  const counts: Record<string, number> = {};
  for (const l of links ?? []) counts[l.tag_id] = (counts[l.tag_id] ?? 0) + 1;
  return {
    tags: (tags ?? []).map((t) => ({ ...t, post_count: counts[t.id] ?? 0 })),
  };
});

export const setPostTags = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        post_id: z.string().uuid(),
        tag_ids: z.array(z.string().uuid()).max(6),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase } = context;
    const { error: delError } = await supabase
      .from("forum_post_tags")
      .delete()
      .eq("post_id", data.post_id);
    if (delError) throw new Error(delError.message);
    if (data.tag_ids.length) {
      const { error } = await supabase
        .from("forum_post_tags")
        .insert(data.tag_ids.map((tag_id) => ({ post_id: data.post_id, tag_id })));
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const getTagsForPosts = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ post_ids: z.array(z.string().uuid()).max(60) }).parse(input))
  .handler(async ({ data }) => {
    if (!data.post_ids.length) return { byPost: {} as Record<string, Array<{ slug: string; name: string }>> };
    const supabase = publicClient();
    const { data: rows } = await supabase
      .from("forum_post_tags")
      .select("post_id, forum_tags(slug, name)")
      .in("post_id", data.post_ids);
    const byPost: Record<string, Array<{ slug: string; name: string }>> = {};
    for (const r of (rows ?? []) as Array<{ post_id: string; forum_tags: { slug: string; name: string } | null }>) {
      if (!r.forum_tags) continue;
      (byPost[r.post_id] ??= []).push({ slug: r.forum_tags.slug, name: r.forum_tags.name });
    }
    return { byPost };
  });

export const listPostsByTag = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().trim().min(1).max(80) }).parse(input))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: tag } = await supabase
      .from("forum_tags")
      .select("id, slug, name, kind")
      .eq("slug", data.slug)
      .maybeSingle();
    type TaggedPost = {
      id: string;
      slug: string;
      title: string;
      body: string;
      post_type: string;
      vote_score: number;
      comment_count: number;
      last_activity_at: string;
      created_at: string;
      user_id: string;
      author: { display_name: string | null; avatar_url: string | null };
    };
    const empty: TaggedPost[] = [];
    if (!tag) return { tag: null as { id: string; slug: string; name: string; kind: string } | null, posts: empty };

    const { data: links } = await supabase
      .from("forum_post_tags")
      .select("post_id")
      .eq("tag_id", tag.id)
      .limit(200);
    const ids = (links ?? []).map((l) => l.post_id);
    if (!ids.length) return { tag, posts: empty };

    const { data: posts } = await supabase
      .from("forum_posts")
      .select("id, slug, title, body, post_type, vote_score, comment_count, last_activity_at, created_at, user_id")
      .in("id", ids)
      .order("last_activity_at", { ascending: false })
      .limit(60);

    const authorIds = Array.from(new Set((posts ?? []).map((p) => p.user_id)));
    const { data: profiles } = authorIds.length
      ? await supabase.from("public_profiles").select("id, display_name, avatar_url").in("id", authorIds)
      : { data: [] as Array<{ id: string; display_name: string | null; avatar_url: string | null }> };
    const authors = new Map((profiles ?? []).map((p) => [p.id, p]));

    return {
      tag,
      posts: (posts ?? []).map((p): TaggedPost => ({
        ...p,
        author: {
          display_name: authors.get(p.user_id)?.display_name ?? null,
          avatar_url: authors.get(p.user_id)?.avatar_url ?? null,
        },
      })),
    };
  });

/* ----------------------------- SUBSCRIPTIONS ---------------------------- */

export const getMySubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        post_id: z.string().uuid().optional(),
        category_id: z.string().uuid().optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: rows } = await supabase
      .from("forum_subscriptions")
      .select("id, post_id, category_id")
      .eq("user_id", userId);
    const list = rows ?? [];
    return {
      thread: data.post_id ? list.some((r) => r.post_id === data.post_id) : false,
      category: data.category_id ? list.some((r) => r.category_id === data.category_id) : false,
    };
  });

export const toggleSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        post_id: z.string().uuid().optional(),
        category_id: z.string().uuid().optional(),
      })
      .refine((v) => Boolean(v.post_id) !== Boolean(v.category_id), "Ange tråd eller kategori")
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const column = data.post_id ? "post_id" : "category_id";
    const value = data.post_id ?? data.category_id!;
    const { data: existing } = await supabase
      .from("forum_subscriptions")
      .select("id")
      .eq("user_id", userId)
      .eq(column, value)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase.from("forum_subscriptions").delete().eq("id", existing.id);
      if (error) throw new Error(error.message);
      return { following: false };
    }
    const { error } = await supabase.from("forum_subscriptions").insert({
      user_id: userId,
      post_id: data.post_id ?? null,
      category_id: data.category_id ?? null,
    });
    if (error) throw new Error(error.message);
    return { following: true };
  });

export const listMySubscriptions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: rows } = await supabase
      .from("forum_subscriptions")
      .select("id, post_id, category_id, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    const postIds = (rows ?? []).map((r) => r.post_id).filter((v): v is string => Boolean(v));
    const catIds = (rows ?? []).map((r) => r.category_id).filter((v): v is string => Boolean(v));

    const [posts, cats] = await Promise.all([
      postIds.length
        ? supabase.from("forum_posts").select("id, slug, title, comment_count").in("id", postIds)
        : Promise.resolve({ data: [] as Array<{ id: string; slug: string; title: string; comment_count: number }> }),
      catIds.length
        ? supabase.from("forum_categories").select("id, slug, name").in("id", catIds)
        : Promise.resolve({ data: [] as Array<{ id: string; slug: string; name: string }> }),
    ]);

    return {
      threads: posts.data ?? [],
      categories: cats.data ?? [],
    };
  });

/* ------------------------- EXPERIENCE REPORTS --------------------------- */

const REPORT_SELECT =
  "id, brand, model, model_year, vehicle_type, moisture_result, moisture_percent, gas_test_result, overall_rating, mileage_km, ownership_years, body, created_at, user_id";

export const listExperienceReports = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data: rows } = await supabase
    .from("vehicle_experience_reports")
    .select(REPORT_SELECT)
    .eq("published", true)
    .order("created_at", { ascending: false })
    .limit(300);

  const reports = rows ?? [];
  const authorIds = Array.from(new Set(reports.map((r) => r.user_id)));
  const { data: profiles } = authorIds.length
    ? await supabase.from("public_profiles").select("id, display_name").in("id", authorIds)
    : { data: [] as Array<{ id: string; display_name: string | null }> };
  const names = new Map((profiles ?? []).map((p) => [p.id, p.display_name]));

  type Row = (typeof reports)[number];
  const groups = new Map<
    string,
    {
      key: string;
      brand: string;
      model: string | null;
      count: number;
      ratingSum: number;
      ratingCount: number;
      dry: number;
      moistureIssues: number;
      gasApproved: number;
      gasIssues: number;
    }
  >();

  for (const r of reports as Row[]) {
    const key = `${r.brand.toLowerCase()}|${(r.model ?? "").toLowerCase()}`;
    const g =
      groups.get(key) ??
      {
        key,
        brand: r.brand,
        model: r.model,
        count: 0,
        ratingSum: 0,
        ratingCount: 0,
        dry: 0,
        moistureIssues: 0,
        gasApproved: 0,
        gasIssues: 0,
      };
    g.count += 1;
    if (r.overall_rating) {
      g.ratingSum += r.overall_rating;
      g.ratingCount += 1;
    }
    if (r.moisture_result === "torr") g.dry += 1;
    if (r.moisture_result === "fuktindikation" || r.moisture_result === "fuktskada") g.moistureIssues += 1;
    if (r.gas_test_result === "godkand") g.gasApproved += 1;
    if (r.gas_test_result === "anmarkning" || r.gas_test_result === "underkand") g.gasIssues += 1;
    groups.set(key, g);
  }

  const summary = Array.from(groups.values())
    .map((g) => ({
      key: g.key,
      brand: g.brand,
      model: g.model,
      count: g.count,
      avg_rating: g.ratingCount ? Math.round((g.ratingSum / g.ratingCount) * 10) / 10 : null,
      dry_share: g.dry + g.moistureIssues ? Math.round((g.dry / (g.dry + g.moistureIssues)) * 100) : null,
      gas_ok_share:
        g.gasApproved + g.gasIssues ? Math.round((g.gasApproved / (g.gasApproved + g.gasIssues)) * 100) : null,
    }))
    .sort((a, b) => b.count - a.count);

  return {
    summary,
    reports: reports.map((r) => ({ ...r, author_name: names.get(r.user_id) ?? null })),
  };
});

export const createExperienceReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        brand: z.string().trim().min(2).max(60),
        model: z.string().trim().max(80).optional(),
        model_year: z.coerce.number().int().min(1960).max(2030).optional(),
        vehicle_type: z.enum(["husbil", "husvagn"]).default("husbil"),
        moisture_result: z.enum(["torr", "fuktindikation", "fuktskada", "ej_testad"]).optional(),
        moisture_percent: z.coerce.number().min(0).max(100).optional(),
        gas_test_result: z.enum(["godkand", "anmarkning", "underkand", "ej_testad"]).optional(),
        overall_rating: z.coerce.number().int().min(1).max(5).optional(),
        mileage_km: z.coerce.number().int().min(0).max(2000000).optional(),
        ownership_years: z.coerce.number().min(0).max(60).optional(),
        body: z.string().trim().max(4000).optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase.from("vehicle_experience_reports").insert({
      user_id: userId,
      brand: data.brand,
      model: data.model || null,
      model_year: data.model_year ?? null,
      vehicle_type: data.vehicle_type,
      moisture_result: data.moisture_result ?? null,
      moisture_percent: data.moisture_percent ?? null,
      gas_test_result: data.gas_test_result ?? null,
      overall_rating: data.overall_rating ?? null,
      mileage_km: data.mileage_km ?? null,
      ownership_years: data.ownership_years ?? null,
      body: data.body || null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });
